import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { connect } from 'react-redux';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { getSingleProduct, UpdateStoreProduct, deleteProduct, resetForm } from '../../actions/adminProducts';
import ProductForm, { ProductFormValues } from './ProductForm';

const EMPTY: ProductFormValues = {
  name: '', description: '', benefits: '', didYouKnow: '',
  productCode: '', origin: '', unitSize: '', category: '',
  subCategory: '', price: '', inStock: 'inStock',
};

const fromProduct = (p: any): ProductFormValues => ({
  name: p.name ?? '',
  description: p.description ?? '',
  benefits: p.benefits ?? '',
  didYouKnow: p.didYouKnow ?? '',
  productCode: p.productCode ?? '',
  origin: p.origin ?? '',
  unitSize: p.unitSize ?? '',
  category: p.category ?? '',
  subCategory: p.subCategory ?? '',
  price: p.price ?? '',
  inStock: p.inStock ? 'inStock' : 'outofStock',
});

const UpdateProduct = ({ adminProducts, getSingleProduct, UpdateStoreProduct, deleteProduct, resetForm }: any) => {
  const history = useHistory();
  const location = useLocation();
  const params = useParams() as any;
  const ap = adminProducts;

  const [values, setValues] = useState<ProductFormValues>(EMPTY);
  const [image, setImage] = useState<File | null>(null);
  const [didYouKnowImage, setDidYouKnowImage] = useState<File | null>(null);
  const [productImageUrl, setProductImageUrl] = useState('');
  const [didYouKnowImageUrl, setDidYouKnowImageUrl] = useState('');
  const [errors, setErrors] = useState<any>({});
  const [productId, setProductId] = useState('');
  const [showUpdated, setShowUpdated] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  useEffect(() => {
    const state = location.state as any;
    if (state?.product) {
      const p = state.product;
      setValues(fromProduct(p));
      setProductImageUrl(p.image ?? '');
      setDidYouKnowImageUrl(p.didYouKnowImage ?? '');
      setProductId(p._id);
    } else {
      getSingleProduct(params.productid);
    }
    return () => { resetForm(); };
  }, []);

  useEffect(() => { setErrors(ap.errors); }, [ap.errors]);

  useEffect(() => {
    const p = ap.product;
    if (!p?.name) return;
    setValues(fromProduct(p));
    setProductImageUrl(p.image ?? '');
    setDidYouKnowImageUrl(p.didYouKnowImage ?? '');
    setProductId(p._id);
  }, [ap.product]);

  useEffect(() => {
    if (ap.productUpdated) {
      setShowUpdated(true);
      const el = document.querySelector('.admin-main-inner');
      if (el) el.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [ap.productUpdated]);

  useEffect(() => {
    if (ap.productDeleted) {
      setTimeout(() => history.push('/adminpanel/products'), 2000);
    }
  }, [ap.productDeleted]);

  const onChange = (e: any) => {
    const name = e.target.name;
    let value = e.target.value;
    if (name === 'price' && value !== '') value = parseFloat(value);
    if (ap.productUpdated) resetForm();
    setValues((prev) => ({ ...prev, [name]: value }));
  };

  const onImageSelect = (e: any) => {
    const fieldName = e.target.name;
    const file = e.target.files[0];
    if (!file) return;
    if (ap.productUpdated) resetForm();
    const reader = new FileReader();
    reader.onloadend = () => {
      if (fieldName === 'image') { setImage(file); setProductImageUrl(reader.result as string); }
      if (fieldName === 'didYouKnowImage') { setDidYouKnowImage(file); setDidYouKnowImageUrl(reader.result as string); }
    };
    reader.readAsDataURL(file);
  };

  const handleFormSubmit = (e: any) => {
    e.preventDefault();
    const formData = new FormData();
    if (image) formData.append('image', image);
    if (didYouKnowImage) formData.append('didYouKnowImage', didYouKnowImage);
    Object.entries(values).forEach(([k, v]) => {
      if (k === 'inStock') { formData.append('inStock', String(v === 'inStock')); return; }
      if (v !== '') formData.append(k, String(v));
    });
    UpdateStoreProduct(formData, productId);
  };

  const title = (location.state as any)?.product?.name ?? params.title?.replace(/-/g, ' ') ?? 'Update Product';

  return (
    <div>
      <Helmet>
        <title>{title} - Frozberries Victoria</title>
        <meta name="description" content={`${title} - Frozberries Victoria`} />
      </Helmet>

      {showUpdated && ap.product && (
        <div className="admin-card" style={{ marginBottom: 16, padding: 16, display: 'flex', alignItems: 'center', gap: 16 }}>
          <img src={ap.product.image} alt={ap.product.name} style={{ height: 60, borderRadius: 6, objectFit: 'cover' }} />
          <div>
            <p style={{ margin: 0, fontWeight: 600 }}>{ap.product.name} updated successfully.</p>
            <button
              className="admin-dropdown-trigger"
              style={{ marginTop: 6 }}
              onClick={() => history.push(`/shop/${ap.product.name.replace(/[^A-Z0-9]+/gi, '-')}/${ap.product._id}`)}
            >
              View in shop
            </button>
          </div>
        </div>
      )}

      {ap.productDeleted && (
        <div className="admin-card" style={{ marginBottom: 16, padding: 16 }}>
          <p style={{ margin: 0, color: '#721c24', fontWeight: 600 }}>Product deleted. Redirecting…</p>
        </div>
      )}

      {ap.loading && !values.name ? (
        <div className="admin-empty-state">
          <i className="fas fa-spinner fa-spin" />
          <p>Loading product…</p>
        </div>
      ) : (
        <div className="admin-card">
          <div className="admin-card-header">
            <p className="admin-card-title">{title}</p>
          </div>
          <div className="admin-card-body">
            <ProductForm
              values={values}
              errors={errors}
              productImageUrl={productImageUrl}
              didYouKnowImageUrl={didYouKnowImageUrl}
              onChange={onChange}
              onImageSelect={onImageSelect}
              onSubmit={handleFormSubmit}
              onBack={() => history.push('/adminpanel/products')}
              submitLabel="Update product"
              loading={ap.loading && !ap.deleteButtonLoading}
              extraActions={
                showDeleteConfirm ? (
                  <>
                    <span style={{ fontSize: '0.82rem', color: '#6d7175', alignSelf: 'center' }}>Sure?</span>
                    <button
                      type="button"
                      className="admin-dropdown-trigger"
                      style={{ background: '#f8d7da', color: '#721c24', borderColor: '#f8d7da' }}
                      onClick={() => deleteProduct(productId)}
                    >
                      {ap.deleteButtonLoading ? 'Deleting…' : 'Yes, delete'}
                    </button>
                    <button type="button" className="admin-dropdown-trigger" onClick={() => setShowDeleteConfirm(false)}>
                      Cancel
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    className="admin-dropdown-trigger"
                    style={{ background: '#f8d7da', color: '#721c24', borderColor: '#f8d7da' }}
                    onClick={() => setShowDeleteConfirm(true)}
                  >
                    Delete product
                  </button>
                )
              }
            />
          </div>
        </div>
      )}
    </div>
  );
};

const mapStateToProps = (state: any) => ({ adminProducts: state.adminProducts });
export default connect(mapStateToProps, { getSingleProduct, UpdateStoreProduct, deleteProduct, resetForm })(UpdateProduct);
